import React, { useState, useMemo } from 'react';
import { Card } from '../../ui/Card';
import { Tooltip } from '../../ui/Tooltip';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';

// 商品データ型定義
interface BeverageProduct {
  小分類: string;
  メーカー名: string;
  商品名: string;
  サイズ: string;
  価格: number;
  棚段番号: number;
}

// カテゴリー別の色設定
const CATEGORY_COLORS = {
  'お茶・紅茶': {
    綾鷹: '#2D5016',           // 濃い緑
    綾鷹濃い緑茶: '#1B3409',   // より濃い緑
    爽健美茶: '#5B8C3A',       // 明るい緑
    やかんの麦茶: '#8B6F47',   // 茶色
    伊右衛門: '#4A7C59',       // 緑
    'お～いお茶': '#5F8A3A',   // 黄緑
    午後の紅茶: '#C85A17',     // オレンジ茶
  },
  '水': {
    いろはす: '#87CEEB',       // スカイブルー
    'GREEN DAKARA': '#90EE90', // ライトグリーン
    天然水: '#4682B4',         // 青
    'SPARKLING': '#00CED1',    // ターコイズ
    ウィルキンソン炭酸水: '#1E90FF', // ドジャーブルー
  },
  'スポーツドリンク': {
    アクエリアス: '#00BFFF',   // ディープスカイブルー
    ポカリスエット: '#87CEFA', // ライトスカイブルー
    カルピスソーダ: '#4169E1', // ロイヤルブルー
  },
  '炭酸飲料': {
    'コカ・コーラ': '#8B0000',     // ダークレッド
    'コカ･コーラゼロ': '#2F4F4F', // ダークグレー
    ファンタオレンジ: '#FF8C00',  // ダークオレンジ
    ファンタグレープ: '#8B008B',  // ダークマゼンタ
    カナダドライジンジャーエール: '#FFD700', // ゴールド
  }
};

// メーカー別のロゴカラー
const MAKER_COLORS = {
  'コカ・コーラ': '#ED1C16',
  'サントリー': '#0033A0',
  '伊藤園': '#006B3C',
  'キリン': '#FFD700',
  'アサヒ飲料': '#003DA5',
  '大塚製薬': '#0072BC',
};

interface BeverageShelfVisualizerProps {
  products?: BeverageProduct[];
  editMode?: boolean;
  onProductClick?: (product: BeverageProduct) => void;
  onLayoutChange?: (products: BeverageProduct[]) => void;
}

const BeverageShelfVisualizer: React.FC<BeverageShelfVisualizerProps> = ({
  products = [],
  editMode = false,
  onProductClick,
  onLayoutChange
}) => {
  const [selectedProduct, setSelectedProduct] = useState<BeverageProduct | null>(null);
  const [hoveredProduct, setHoveredProduct] = useState<BeverageProduct | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'realistic'>('realistic');

  // デフォルトの商品データ
  const defaultProducts: BeverageProduct[] = [
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹", "サイズ": "525ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹", "サイズ": "525ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹", "サイズ": "525ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹濃い緑茶", "サイズ": "650ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹濃い緑茶", "サイズ": "650ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "綾鷹濃い緑茶", "サイズ": "650ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "爽健美茶", "サイズ": "600ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "爽健美茶", "サイズ": "600ml", "価格": 150, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "やかんの麦茶", "サイズ": "650ml", "価格": 140, "棚段番号": 1},
    {"小分類": "お茶・紅茶", "メーカー名": "コカ・コーラ", "商品名": "やかんの麦茶", "サイズ": "650ml", "価格": 140, "棚段番号": 1},
    
    {"小分類": "お茶・紅茶", "メーカー名": "サントリー", "商品名": "伊右衛門", "サイズ": "600ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "サントリー", "商品名": "伊右衛門", "サイズ": "600ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "伊藤園", "商品名": "お～いお茶", "サイズ": "600ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "伊藤園", "商品名": "お～いお茶", "サイズ": "600ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 無糖", "サイズ": "500ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 無糖", "サイズ": "500ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 ストレート", "サイズ": "500ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 ストレート", "サイズ": "500ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 ミルクティー", "サイズ": "500ml", "価格": 150, "棚段番号": 2},
    {"小分類": "お茶・紅茶", "メーカー名": "キリン", "商品名": "午後の紅茶 ミルクティー", "サイズ": "500ml", "価格": 150, "棚段番号": 2},
    
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはす", "サイズ": "540ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはす", "サイズ": "540ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはす", "サイズ": "540ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはすマスカット", "サイズ": "540ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはすマスカット", "サイズ": "540ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "いろはすマスカット", "サイズ": "540ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "GREEN DAKARA", "サイズ": "500ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "コカ・コーラ", "商品名": "GREEN DAKARA", "サイズ": "500ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "アサヒ飲料", "商品名": "ウィルキンソン炭酸水", "サイズ": "500ml", "価格": 140, "棚段番号": 3},
    {"小分類": "水", "メーカー名": "アサヒ飲料", "商品名": "ウィルキンソン炭酸水", "サイズ": "500ml", "価格": 140, "棚段番号": 3},
    
    {"小分類": "水", "メーカー名": "サントリー", "商品名": "天然水", "サイズ": "500ml", "価格": 140, "棚段番号": 4},
    {"小分類": "水", "メーカー名": "サントリー", "商品名": "天然水", "サイズ": "500ml", "価格": 140, "棚段番号": 4},
    {"小分類": "水", "メーカー名": "サントリー", "商品名": "天然水SPARKLING", "サイズ": "500ml", "価格": 140, "棚段番号": 4},
    {"小分類": "水", "メーカー名": "サントリー", "商品名": "天然水SPARKLING", "サイズ": "500ml", "価格": 140, "棚段番号": 4},
    {"小分類": "スポーツドリンク", "メーカー名": "コカ・コーラ", "商品名": "アクエリアス", "サイズ": "500ml", "価格": 150, "棚段番号": 4},
    {"小分類": "スポーツドリンク", "メーカー名": "コカ・コーラ", "商品名": "アクエリアス", "サイズ": "500ml", "価格": 150, "棚段番号": 4},
    {"小分類": "スポーツドリンク", "メーカー名": "大塚製薬", "商品名": "ポカリスエット", "サイズ": "500ml", "価格": 150, "棚段番号": 4},
    {"小分類": "スポーツドリンク", "メーカー名": "大塚製薬", "商品名": "ポカリスエット", "サイズ": "500ml", "価格": 150, "棚段番号": 4},
    {"小分類": "スポーツドリンク", "メーカー名": "アサヒ飲料", "商品名": "カルピスソーダ", "サイズ": "500ml", "価格": 150, "棚段番号": 4},
    {"小分類": "スポーツドリンク", "メーカー名": "アサヒ飲料", "商品名": "カルピスソーダ", "サイズ": "500ml", "価格": 150, "棚段番号": 4},
    
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "コカ・コーラ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "コカ・コーラ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "コカ･コーラゼロ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "コカ･コーラゼロ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "ファンタオレンジ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "ファンタオレンジ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "ファンタグレープ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "ファンタグレープ", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "カナダドライジンジャーエール", "サイズ": "500ml", "価格": 150, "棚段番号": 5},
    {"小分類": "炭酸飲料", "メーカー名": "コカ・コーラ", "商品名": "カナダドライジンジャーエール", "サイズ": "500ml", "価格": 150, "棚段番号": 5}
  ];

  const displayProducts = products.length > 0 ? products : defaultProducts;

  // 棚段ごとに商品をグループ化
  const productsByShelf = useMemo(() => {
    const grouped: { [key: number]: BeverageProduct[] } = {};
    for (let i = 1; i <= 5; i++) {
      grouped[i] = displayProducts.filter(p => p.棚段番号 === i);
    }
    return grouped;
  }, [displayProducts]);

  // 商品の色を取得
  const getProductColor = (product: BeverageProduct): string => {
    const categoryColors = CATEGORY_COLORS[product.小分類 as keyof typeof CATEGORY_COLORS];
    if (categoryColors && product.商品名 in categoryColors) {
      return categoryColors[product.商品名 as keyof typeof categoryColors];
    }
    // デフォルトカラー
    switch (product.小分類) {
      case 'お茶・紅茶': return '#4A7C59';
      case '水': return '#4682B4';
      case 'スポーツドリンク': return '#00BFFF';
      case '炭酸飲料': return '#FF6347';
      default: return '#808080';
    }
  };

  // 商品名を短縮表示
  const getShortName = (name: string): string => {
    // 特定の商品名の短縮形
    const shortNames: { [key: string]: string } = {
      '綾鷹濃い緑茶': '綾鷹濃い',
      '午後の紅茶 無糖': '午後の紅茶\n無糖',
      '午後の紅茶 ストレート': '午後の紅茶\nストレート',
      '午後の紅茶 ミルクティー': '午後の紅茶\nミルク',
      'いろはすマスカット': 'いろはす\nマスカット',
      '天然水SPARKLING': '天然水\nSPARKLING',
      'ウィルキンソン炭酸水': 'ウィルキンソン\n炭酸水',
      'カナダドライジンジャーエール': 'カナダドライ\nジンジャー',
      'コカ･コーラゼロ': 'コカ・コーラ\nゼロ',
      'ファンタオレンジ': 'ファンタ\nオレンジ',
      'ファンタグレープ': 'ファンタ\nグレープ',
    };
    return shortNames[name] || name;
  };

  // 商品カードのレンダリング
  const renderProduct = (product: BeverageProduct, index: number) => {
    const isSelected = selectedProduct === product;
    const isHovered = hoveredProduct === product;
    const productColor = getProductColor(product);
    const shortName = getShortName(product.商品名);
    const nameLines = shortName.split('\n');

    return (
      <Tooltip
        key={`${product.商品名}-${index}`}
        content={
          <div style={{ fontSize: '0.85rem', padding: spacing.xs }}>
            <div style={{ fontWeight: 600, marginBottom: spacing.xs }}>{product.商品名}</div>
            <div>メーカー: {product.メーカー名}</div>
            <div>カテゴリー: {product.小分類}</div>
            <div>サイズ: {product.サイズ}</div>
            <div>価格: ¥{product.価格}</div>
            <div>棚段: {product.棚段番号}段目</div>
          </div>
        }
      >
        <div
          className="product-card"
          onClick={() => {
            setSelectedProduct(product);
            onProductClick?.(product);
          }}
          onMouseEnter={() => setHoveredProduct(product)}
          onMouseLeave={() => setHoveredProduct(null)}
          style={{
            width: '80px',
            height: '120px',
            backgroundColor: productColor,
            border: `2px solid ${isSelected ? colors.accent.blue : isHovered ? colors.ui.borderHover : 'white'}`,
            borderRadius: borderRadius.md,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            boxShadow: isSelected ? shadows.lg : isHovered ? shadows.md : shadows.sm,
            padding: spacing.xs,
            position: 'relative',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            textAlign: 'center',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
          }}
        >
          {/* メーカーバッジ */}
          <div
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              backgroundColor: MAKER_COLORS[product.メーカー名 as keyof typeof MAKER_COLORS] || '#666',
              color: 'white',
              fontSize: '0.5rem',
              padding: '2px 4px',
              borderRadius: '4px',
              fontWeight: 700,
            }}
          >
            {product.メーカー名.substring(0, 3)}
          </div>

          {/* 商品名 */}
          <div style={{ marginTop: spacing.sm }}>
            {nameLines.map((line, i) => (
              <div key={i} style={{ lineHeight: 1.2 }}>
                {line}
              </div>
            ))}
          </div>

          {/* サイズ */}
          <div style={{
            fontSize: '1rem',
            fontWeight: 700,
            marginTop: spacing.xs,
          }}>
            {product.サイズ}
          </div>

          {/* 価格 */}
          <div style={{
            position: 'absolute',
            bottom: '4px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
            fontSize: '0.65rem',
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: 600,
          }}>
            ¥{product.価格}
          </div>
        </div>
      </Tooltip>
    );
  };

  return (
    <Card className="beverage-shelf-visualizer">
      {/* ヘッダー */}
      <div style={{
        padding: spacing.md,
        borderBottom: `1px solid ${colors.ui.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.text.primary }}>
          飲料棚割りビジュアライザー
        </h3>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          <Button
            size="small"
            variant={viewMode === 'realistic' ? 'primary' : 'secondary'}
            onClick={() => setViewMode('realistic')}
          >
            リアル表示
          </Button>
          <Button
            size="small"
            variant={viewMode === 'grid' ? 'primary' : 'secondary'}
            onClick={() => setViewMode('grid')}
          >
            グリッド表示
          </Button>
        </div>
      </div>

      {/* 棚割り表示エリア */}
      <div style={{
        padding: spacing.lg,
        backgroundColor: colors.ui.bgSecondary,
        minHeight: '600px',
      }}>
        {/* 棚の外枠 */}
        <div style={{
          backgroundColor: 'white',
          border: `3px solid ${colors.ui.border}`,
          borderRadius: borderRadius.lg,
          padding: spacing.md,
          boxShadow: shadows.lg,
        }}>
          {/* 各棚段の表示 */}
          {[1, 2, 3, 4, 5].map((shelfNumber) => (
            <div key={shelfNumber} style={{ marginBottom: shelfNumber < 5 ? spacing.md : 0 }}>
              {/* 棚段番号 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: spacing.xs,
              }}>
                <div style={{
                  backgroundColor: colors.ui.bgTertiary,
                  color: colors.text.primary,
                  padding: '4px 12px',
                  borderRadius: borderRadius.sm,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  marginRight: spacing.sm,
                }}>
                  {shelfNumber}段
                </div>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: colors.ui.border,
                }}></div>
              </div>

              {/* 商品配置エリア */}
              <div style={{
                display: 'flex',
                gap: spacing.xs,
                padding: spacing.sm,
                backgroundColor: colors.ui.bgSecondary,
                borderRadius: borderRadius.md,
                minHeight: '140px',
                alignItems: 'center',
                overflowX: 'auto',
                borderBottom: `3px solid ${colors.ui.borderStrong}`,
              }}>
                {productsByShelf[shelfNumber].map((product, index) => 
                  renderProduct(product, index)
                )}
              </div>
            </div>
          ))}

          {/* 床面表示 */}
          <div style={{
            height: '20px',
            background: `linear-gradient(to bottom, ${colors.ui.border}, ${colors.ui.bgTertiary})`,
            borderRadius: `0 0 ${borderRadius.md} ${borderRadius.md}`,
            marginTop: spacing.sm,
          }}></div>
        </div>
      </div>

      {/* 選択商品情報 */}
      {selectedProduct && (
        <div style={{
          padding: spacing.md,
          borderTop: `1px solid ${colors.ui.border}`,
          backgroundColor: colors.ui.bgSecondary,
        }}>
          <h4 style={{ fontWeight: 600, marginBottom: spacing.sm }}>選択中の商品</h4>
          <div style={{ display: 'flex', gap: spacing.lg }}>
            <div>
              <strong>商品名:</strong> {selectedProduct.商品名}
            </div>
            <div>
              <strong>メーカー:</strong> {selectedProduct.メーカー名}
            </div>
            <div>
              <strong>サイズ:</strong> {selectedProduct.サイズ}
            </div>
            <div>
              <strong>価格:</strong> ¥{selectedProduct.価格}
            </div>
            <div>
              <strong>棚段:</strong> {selectedProduct.棚段番号}段目
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BeverageShelfVisualizer;
